import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { CreateInvoiceJustificationPayload } from 'contracts/tracking-v2/invoice-justifications';
import { useInvoiceJustificationOptions } from 'hooks/api/options/general';
import { useInvoiceStore } from 'pages/tracking-v2/invoice-details-page/hooks';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
} from 'components/ui/dialog';
import { ComboboxGs, TextareaGs } from 'components/ui/Forms';
import { Form, FormField } from 'components/ui/form';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';

import { formSchema, type FormSchema } from './form-schema';

type Ref = {
  open: () => void;
  close: () => void;
}

export type CreateInvoiceJustificationDialogRef = Ref

type Props = {
  onSubmit: (payload: CreateInvoiceJustificationPayload) => void
  loading?: boolean
}

export const CreateInvoiceJustificationDialog = React.forwardRef<Ref, Props>(({
                                                                                onSubmit,
                                                                                loading,
                                                                              }, ref) => {
  const { invoice } = useInvoiceStore();
  const justificationOptions = useInvoiceJustificationOptions();

  const [open, setOpen] = React.useState(false);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    if (!invoice?.id) return;
    onSubmit({ ...formData, invoiceId: invoice.id });
  }, [onSubmit, invoice?.id]);

  const resetForm = React.useCallback(() => {
    form.reset({ justificationId: undefined, description: '' }, {
      keepValues: false,
    });
  }, []);

  const onOpenChange = React.useCallback((open: boolean) => {
    resetForm();
    setOpen(open);
  }, [resetForm]);

  React.useImperativeHandle(ref, () => ({
    open: () => onOpenChange(true),
    close: () => onOpenChange(false),
  }), [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={'w-full max-w-[540px]'}>
        <DialogHeader className={'mb-6'}>
          <DialogTitle>Adicionar justificativa</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onFormSubmit)} className={'w-full'}>
            <div className={'flex flex-col gap-4'}>
              <FormField
                control={form.control}
                name={'justificationId'}
                render={({ field }) => (
                  <ComboboxGs
                    label={'Justificativa'}
                    form={form}
                    fieldName={field.name}
                    fieldValue={field.value}
                    content={justificationOptions.singleSelectOptions}
                    loading={justificationOptions.loading}
                    portal={false}
                  />
                )}
              />
              <FormField
                control={form.control}
                name={'description'}
                render={({ field }) => (
                  <TextareaGs
                    {...field}
                    label={'Comentário'}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div className={'grid grid-cols-2 gap-4 mt-6'}>
              <Button.Root
                type={'button'}
                size={'sm'}
                variant={'secondary'}
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button.Root>
              <Button.Root type={'submit'} size={'sm'} disabled={loading}>
                {loading ? <ActivityIndicator /> : 'Salvar'}
              </Button.Root>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
CreateInvoiceJustificationDialog.displayName = 'CreateInvoiceJustificationDialog';